import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Download } from "lucide-react";
import html2canvas from "html2canvas";

export default function TypingStats({ metrics, onRestart, onPrev, onNext }) {
  if (!metrics) return null;

  const data = [
    {
      name: "Results",
      WPM: metrics.wpm,
      CPM: metrics.cpm,
      KPH: metrics.kph,
      Accuracy: metrics.accuracy,
      Errors: metrics.errors,
      Time: parseFloat(metrics.time),
    },
  ];

  const createScreenshotElement = () => {
    // Create a simplified version for screenshot with basic CSS
    const container = document.createElement('div');
    container.style.width = '800px';
    container.style.backgroundColor = '#000000';
    container.style.padding = '24px';
    container.style.fontFamily = 'Arial, sans-serif';
    container.style.color = '#ffffff';

    // Title
    const title = document.createElement('h2');
    title.textContent = 'Your Final Typing Results:';
    title.style.color = '#ffffff';
    title.style.fontSize = '24px';
    title.style.fontWeight = 'bold';
    title.style.textAlign = 'center';
    title.style.marginBottom = '24px';
    container.appendChild(title);

    // Stats Grid
    const statsGrid = document.createElement('div');
    statsGrid.style.display = 'grid';
    statsGrid.style.gridTemplateColumns = 'repeat(5, 1fr)';
    statsGrid.style.gap = '16px';
    statsGrid.style.marginBottom = '24px';

    const stats = [
      { label: 'WPM', value: metrics.wpm, color: '#00ff00', desc: 'Words Per Minute' },
      { label: 'CPM', value: metrics.cpm, color: '#00ffff', desc: 'Chars/Min' },
      { label: 'KPH', value: metrics.kph, color: '#a855f7', desc: 'Keystrokes/Hour' },
      { label: 'Accuracy', value: `${metrics.accuracy}%`, color: '#ffa500', desc: 'Accuracy' },
      { label: 'Errors', value: metrics.errors, color: '#ff4d4d', desc: 'Errors' },
    ];

    stats.forEach(stat => {
      const statCard = document.createElement('div');
      statCard.style.backgroundColor = '#1f2937';
      statCard.style.padding = '16px';
      statCard.style.borderRadius = '8px';
      statCard.style.textAlign = 'center';
      statCard.style.border = '1px solid #374151';

      const value = document.createElement('div');
      value.textContent = stat.value;
      value.style.color = stat.color;
      value.style.fontSize = '24px';
      value.style.fontWeight = 'bold';
      value.style.marginBottom = '4px';

      const label = document.createElement('div');
      label.textContent = stat.label;
      label.style.color = '#d1d5db';
      label.style.fontSize = '14px';
      label.style.marginBottom = '2px';

      const desc = document.createElement('div');
      desc.textContent = stat.desc;
      desc.style.color = '#9ca3af';
      desc.style.fontSize = '12px';

      statCard.appendChild(value);
      statCard.appendChild(label);
      statCard.appendChild(desc);
      statsGrid.appendChild(statCard);
    });

    container.appendChild(statsGrid);

    // Summary text
    const summary = document.createElement('div');
    summary.style.backgroundColor = '#000000';
    summary.style.padding = '16px';
    summary.style.borderRadius = '8px';
    summary.style.border = '1px solid #374151';
    summary.style.marginBottom = '24px';
    summary.style.textAlign = 'center';

    const summaryTitle = document.createElement('h3');
    summaryTitle.textContent = 'Performance Summary';
    summaryTitle.style.color = '#ffffff';
    summaryTitle.style.fontSize = '18px';
    summaryTitle.style.marginBottom = '12px';
    summaryTitle.style.fontWeight = 'bold';

    const summaryText = document.createElement('div');
    summaryText.innerHTML = `
      <div style="color: #ffffff; margin-bottom: 8px;">
        🚀 <strong>${metrics.wpm} WPM</strong> • ${metrics.accuracy}% Accuracy
      </div>
      <div style="color: #d1d5db; font-size: 14px;">
        ${metrics.cpm} CPM • ${metrics.kph} KPH • ${metrics.errors} Errors • ${metrics.time}s
      </div>
      <div style="color: #9ca3af; font-size: 12px; margin-top: 8px;">
        Generated by typer.punote.com | product of punote.com
      </div>
    `;

    summary.appendChild(summaryTitle);
    summary.appendChild(summaryText);
    container.appendChild(summary);

    return container;
  };

  const captureScreenshot = async () => {
    try {
      const screenshotElement = createScreenshotElement();
      
      screenshotElement.style.position = 'fixed';
      screenshotElement.style.left = '-9999px';
      screenshotElement.style.top = '-9999px';
      document.body.appendChild(screenshotElement);

      const canvas = await html2canvas(screenshotElement, {
        backgroundColor: '#000000',
        scale: 2,
        useCORS: true,
        logging: false,
        width: screenshotElement.offsetWidth,
        height: screenshotElement.offsetHeight,
      });

      document.body.removeChild(screenshotElement);
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.error("Error capturing screenshot:", error);
      return null;
    }
  };

  const downloadScreenshot = async () => {
    const screenshot = await captureScreenshot();
    if (screenshot) {
      const link = document.createElement("a");
      link.download = `typing-results-${metrics.wpm}wpm-${metrics.accuracy}accuracy.png`;
      link.href = screenshot;
      link.click();
    } else {
      alert('Failed to capture screenshot. Please try again.');
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-2 md:p-4 ">
      <div>
        <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 md:mt-0 text-white text-center mt-64">
          Your Final Typing Results:
        </h2>

        {/* Stats Cards - Mobile responsive grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 mb-4 md:mb-6">
          <div className="bg-gray-800 p-2 md:p-4 rounded-lg text-center">
            <div className="text-lg md:text-2xl font-bold text-green-400">{metrics.wpm}</div>
            <div className="text-gray-300 text-xs md:text-sm">WPM</div>
            <div className="text-gray-400 text-xs hidden md:block">Words Per Minute</div>
          </div>
          <div className="bg-gray-800 p-2 md:p-4 rounded-lg text-center">
            <div className="text-lg md:text-2xl font-bold text-blue-400">{metrics.cpm}</div>
            <div className="text-gray-300 text-xs md:text-sm">CPM</div>
            <div className="text-gray-400 text-xs hidden md:block">Chars/Min</div>
          </div>
          <div className="bg-gray-800 p-2 md:p-4 rounded-lg text-center">
            <div className="text-lg md:text-2xl font-bold text-purple-400">
              {metrics.kph}
            </div>
            <div className="text-gray-300 text-xs md:text-sm">KPH</div>
            <div className="text-gray-400 text-xs hidden md:block">Keystrokes/Hour</div>
          </div>
          <div className="bg-gray-800 p-2 md:p-4 rounded-lg text-center">
            <div className="text-lg md:text-2xl font-bold text-yellow-400">
              {metrics.accuracy}%
            </div>
            <div className="text-gray-300 text-xs md:text-sm">Accuracy</div>
          </div>
          <div className="bg-gray-800 p-2 md:p-4 rounded-lg text-center">
            <div className="text-lg md:text-2xl font-bold text-red-400">
              {metrics.errors}
            </div>
            <div className="text-gray-300 text-xs md:text-sm">Errors</div>
          </div>
        </div>

        {/* Charts - Stack vertically on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
          <div className="bg-black p-3 md:p-4 rounded-lg ">
            <h3 className="text-white mb-2 md:mb-3 text-center text-sm md:text-base">Speed Metrics</h3>
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={data}>
                <CartesianGrid stroke="#555" strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="#fff" fontSize={12} />
                <YAxis stroke="#fff" fontSize={12} />
                <Tooltip
                  contentStyle={{ 
                    backgroundColor: "#1f2937", 
                    border: "none",
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line
                  type="monotone"
                  dataKey="WPM"
                  stroke="#00ff00"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="CPM"
                  stroke="#00ffff"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="KPH"
                  stroke="#a855f7"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-black p-3 md:p-4 rounded-lg">
            <h3 className="text-white mb-2 md:mb-3 text-center text-sm md:text-base">Accuracy & Time</h3>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={data}>
                <CartesianGrid stroke="#555" strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="#fff" fontSize={12} />
                <YAxis stroke="#fff" fontSize={12} />
                <Tooltip
                  contentStyle={{ 
                    backgroundColor: "#1f2937", 
                    border: "none",
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="Accuracy" fill="#ffa500" />
                <Bar dataKey="Errors" fill="#ff4d4d" />
                <Bar dataKey="Time" fill="#22d3ee" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Navigation Buttons - Stack vertically on mobile */}
      <div className="flex flex-col md:flex-row gap-2 md:gap-4 mb-4 md:mb-6 justify-center">
        <button
          onClick={onPrev}
          className="px-4 md:px-6 py-2 md:py-3 bg-green-800 text-white rounded-3xl hover:bg-green-900 transition-colors font-medium cursor-pointer text-sm md:text-base"
        >
          Previous Lesson
        </button>
        <button
          onClick={onRestart}
          className="px-4 md:px-6 py-2 md:py-3 bg-orange-600 text-white rounded-3xl hover:bg-orange-700 transition-colors font-medium cursor-pointer text-sm md:text-base"
        >
          Restart Lesson
        </button>
        <button
          onClick={onNext}
          className="px-4 md:px-6 py-2 md:py-3 bg-blue-700 text-white rounded-3xl hover:bg-blue-800 transition-colors font-medium cursor-pointer text-sm md:text-base"
        >
          Next Lesson
        </button>
      </div>

      {/* Download Button */}
      <div className="flex justify-center">
        <button
          onClick={downloadScreenshot}
          className="flex items-center gap-2 px-4 md:px-8 py-2 bg-transparent border text-white hover:bg-white hover:text-black rounded-2xl transition-all font-medium cursor-pointer text-xs md:text-sm"
        >
          <Download className="w-4 h-4 md:w-5 md:h-5" />
          Download Results
        </button>
      </div>
    </div>
  );
}